import type { FEDispatchSelection } from '@/components/forms/parentTicket/FEDispatchFlow'
import {
  isAdditionalFeRole,
  isPrimaryFeRole,
  resolveAdditionalFeUserIds,
} from '@/components/forms/parentTicket/feDispatchRole'
import { parentLevelFeSchedules } from '@/components/forms/parentTicket/feDispatchUtils'
import type { ParentTicketWithMeta } from '@/store/parentTicket'
import type { FESchedule } from '@/types/parentTicket'
import { TicketService } from '@/lib/services/ticket'
import type { DispatchExistingScheduleIds } from '@/components/forms/parentTicket/feDispatchPersistence'

function uniquePositiveIds(ids: Array<number | null | undefined>): number[] {
  return [...new Set(ids.map(Number).filter((id) => id > 0))]
}

function scheduleIdForFeUser(
  schedules: FESchedule[],
  feUserId: number,
  role?: 'primary' | 'additional' | 'secondary',
  scheduleDate?: string | null,
): number | null {
  const targetFe = Number(feUserId)
  const matches = schedules.filter((s) => {
    if (Number(s.fe_user_id) !== targetFe) return false
    if (role == null) return true
    if (role === 'primary') return isPrimaryFeRole(s.role)
    return isAdditionalFeRole(s.role)
  })
  if (matches.length === 0) return null
  if (scheduleDate) {
    const day = scheduleDate.slice(0, 10)
    const onDay = matches.find((s) => String(s.schedule_date ?? '').slice(0, 10) === day)
    if (onDay?.id != null) return onDay.id
  }
  return matches[0]?.id ?? null
}

function orderedAdditionalFeUserIds(
  meta: ParentTicketWithMeta,
  primaryFeUserId: number | null,
  dispatchAdditionalFes: FEDispatchSelection[],
): number[] {
  if (dispatchAdditionalFes.length > 0) {
    return uniquePositiveIds(dispatchAdditionalFes.map((b) => b.feUserId))
  }

  const fromTicket = resolveAdditionalFeUserIds(meta)
  if (fromTicket.length > 0) {
    return uniquePositiveIds(
      fromTicket.filter((id) => primaryFeUserId == null || Number(id) !== primaryFeUserId),
    )
  }

  const engineers = meta.additionalEngineers ?? meta.backupEngineers ?? []
  if (engineers.length > 0) {
    return uniquePositiveIds(engineers.map((fe) => fe?.user_id))
  }

  const parentSchedules = parentLevelFeSchedules(meta.feSchedules ?? [])
  return uniquePositiveIds(
    parentSchedules
      .filter((s) => isAdditionalFeRole(s.role) && s.fe_user_id !== primaryFeUserId)
      .map((s) => s.fe_user_id),
  )
}

/** Resolve parent-level FE schedule IDs aligned with UI primary/additional slots. */
export function resolveDispatchScheduleIds(
  meta: ParentTicketWithMeta | undefined,
  dispatchPrimary: FEDispatchSelection | null,
  dispatchAdditionalFes: FEDispatchSelection[] = [],
  schedulesOverride?: FESchedule[],
): DispatchExistingScheduleIds {
  const parentSchedules = parentLevelFeSchedules(schedulesOverride ?? meta?.feSchedules ?? [])

  const primaryFeUserId =
    dispatchPrimary?.feUserId ??
    meta?.primaryEngineer?.user_id ??
    (meta?.primaryEngineerId != null ? Number(meta.primaryEngineerId) : null) ??
    null

  const primaryFromRole =
    parentSchedules.find((s) => isPrimaryFeRole(s.role)) ??
    (primaryFeUserId != null
      ? parentSchedules.find((s) => Number(s.fe_user_id) === Number(primaryFeUserId))
      : null) ??
    parentSchedules[0] ??
    null

  const primary =
    (primaryFeUserId != null
      ? scheduleIdForFeUser(
          parentSchedules,
          primaryFeUserId,
          'primary',
          dispatchPrimary?.scheduleDate,
        )
      : null) ??
    primaryFromRole?.id ??
    null

  const additionalFeUserIds = orderedAdditionalFeUserIds(
    meta ?? ({} as ParentTicketWithMeta),
    primaryFeUserId,
    dispatchAdditionalFes,
  )
  const additionalFes = additionalFeUserIds
    .map((feUserId, index) =>
      scheduleIdForFeUser(
        parentSchedules,
        feUserId,
        'additional',
        dispatchAdditionalFes[index]?.scheduleDate,
      ),
    )
    .filter((id): id is number => id != null)

  return { primary, additionalFes }
}

/** Fetch live parent-level schedules and merge with cached ticket store IDs. */
export async function resolveDispatchScheduleIdsForClear(
  ticketId: string,
  meta: ParentTicketWithMeta | undefined,
  dispatchPrimary: FEDispatchSelection | null,
  dispatchAdditionalFes: FEDispatchSelection[] = [],
  fallbackIds: DispatchExistingScheduleIds = { primary: null, additionalFes: [] },
): Promise<DispatchExistingScheduleIds> {
  const cached = resolveDispatchScheduleIds(meta, dispatchPrimary, dispatchAdditionalFes)

  const liveRes = await TicketService.getFESchedules(ticketId)
  if (!liveRes.success) {
    // Network/API failure — fall back to meta + caller-provided IDs.
    return {
      primary: cached.primary ?? fallbackIds.primary,
      additionalFes: cached.additionalFes.length > 0 ? cached.additionalFes : fallbackIds.additionalFes,
    }
  }

  // Live confirmed empty (e.g. after Clear) — do not reuse stale cached schedule IDs.
  if (!liveRes.data?.length) {
    return { primary: null, additionalFes: [] }
  }

  const live = resolveDispatchScheduleIds(meta, dispatchPrimary, dispatchAdditionalFes, liveRes.data)

  return {
    primary: live.primary ?? cached.primary ?? fallbackIds.primary,
    additionalFes: uniquePositiveIds([
      ...live.additionalFes,
      ...cached.additionalFes,
      ...fallbackIds.additionalFes,
    ]),
  }
}

/** All parent-level schedule IDs on a ticket (for primary clear). */
export function allParentLevelScheduleIds(schedules: FESchedule[]): number[] {
  return parentLevelFeSchedules(schedules)
    .map((s) => s.id)
    .filter((id): id is number => id != null)
}

/** Collect every schedule ID to remove when clearing primary FE dispatch. */
export function collectPrimaryClearScheduleIds(
  liveSchedules: FESchedule[],
  cached: DispatchExistingScheduleIds,
  metaSchedules: FESchedule[] = [],
  engineerUserIds: number[] = [],
): number[] {
  const engineerSet = new Set(engineerUserIds.map(Number).filter((id) => id > 0))

  const fromEngineerMatch = liveSchedules
    .filter((s) => s.id != null && engineerSet.has(Number(s.fe_user_id)))
    .map((s) => s.id as number)

  return uniquePositiveIds([
    ...allParentLevelScheduleIds(liveSchedules),
    ...allParentLevelScheduleIds(metaSchedules),
    ...(cached.primary != null ? [cached.primary] : []),
    ...cached.additionalFes,
    ...fromEngineerMatch,
  ])
}
